// src/pages/Unsubscribe.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { handleEmailUnsubscribe, handlePushUnsubscribe } from '../api/salonApi';
import { getSalonConfig } from '../api/salonConfigService';
import '../styles/unsubscribe.css';

function Unsubscribe() {
  const { salonId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [salonConfig, setSalonConfig] = useState(null);
  
  // Determine type based on URL path
  const isEmailUnsubscribe = location.pathname.includes('unsubscribe-email');
  const isPushUnsubscribe = location.pathname.includes('unsubscribe-push');
  
  // Get customer ID from URL parameters
  const customerId = searchParams.get('customer');

  useEffect(() => {
    const unsubscribeCustomer = async () => {
      try {
        setLoading(true);
        
        // Load salon configuration for branding - uses salonConfigService which now points to 'salons' collection
        const config = await getSalonConfig(salonId);
        setSalonConfig(config);
        
        if (!customerId) {
          setError('Invalid unsubscribe link. Please contact the salon directly.');
          return;
        }
        
        // Handle the unsubscribe based on type
        if (isEmailUnsubscribe) {
          await handleEmailUnsubscribe(customerId, salonId);
        } else if (isPushUnsubscribe) {
          await handlePushUnsubscribe(customerId, salonId);
        } else {
          setError('Invalid unsubscribe type. Please contact the salon directly.');
          return;
        }
        
        setSuccess(true);
        
      } catch (err) {
        console.error('Error unsubscribing:', err);
        setError('Failed to unsubscribe. Please try again or contact the salon directly.');
      } finally {
        setLoading(false);
      }
    };
    
    unsubscribeCustomer();
  }, [salonId, customerId, isEmailUnsubscribe, isPushUnsubscribe]);

  if (loading) {
    return (
      <div className="unsubscribe-container">
        <div className="unsubscribe-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your request...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="unsubscribe-container">
      <div className="unsubscribe-card">
        {salonConfig?.branding?.logo?.url && (
          <div className="salon-logo">
            <img 
              src={salonConfig.branding.logo.url} 
              alt={salonConfig.branding.name} 
              className="logo-image"
            />
          </div>
        )}
        
        <h1>
          {isEmailUnsubscribe ? 'Email Unsubscribe' : 'Push Notifications Unsubscribe'}
        </h1>
        
        {success ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Successfully Unsubscribed</h2>
            <p>
              You have been successfully unsubscribed from{' '}
              {isEmailUnsubscribe ? 'email marketing communications' : 'push notifications'} from{' '}
              <strong>{salonConfig?.branding?.name || 'this salon'}</strong>.
            </p>
            <p>
              {isEmailUnsubscribe 
                ? 'You will no longer receive promotional emails, offers, or marketing messages.'
                : 'You will no longer receive promotional push notifications on your device.'
              }
            </p>
            <div className="info-box">
              <h3>Important Notes:</h3>
              <ul>
                <li>
                  You will still receive appointment{' '}
                  {isEmailUnsubscribe ? 'confirmations and service-related emails' : 'reminders and service-related notifications'}
                </li>
                <li>This change may take up to 24 hours to take effect</li>
                <li>
                  You can re-{isEmailUnsubscribe ? 'subscribe' : 'enable push notifications'} anytime by updating your preferences in your{' '}
                  {isEmailUnsubscribe ? 'customer account' : 'device settings or customer account'}
                </li>
                {isPushUnsubscribe && (
                  <li>You may still receive email communications if you haven't unsubscribed from those separately</li>
                )}
              </ul>
            </div>
            
            {isPushUnsubscribe && (
              <div className="alternative-notifications">
                <h3>Prefer Email Updates?</h3>
                <p>
                  If you'd prefer to receive our updates via email instead of push notifications, 
                  you can manage your communication preferences in your customer account.
                </p>
                <a 
                  href={`/BespokeBeauty/${salonId}/customer/home`} 
                  className="dashboard-link"
                >
                  Go to My Account
                </a>
              </div>
            )}
            
            {salonConfig?.contact && (
              <div className="contact-info">
                <h3>{isPushUnsubscribe ? 'Stay Connected' : 'Still want to stay connected?'}</h3>
                <p>Follow us on social media for updates{isEmailUnsubscribe ? ' and offers' : ''}:</p>
                <div className="social-links">
                  {salonConfig.contact.socialMedia?.facebook && (
                    <a 
                      href={salonConfig.contact.socialMedia.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link facebook"
                    >
                      Facebook
                    </a>
                  )}
                  {salonConfig.contact.socialMedia?.instagram && (
                    <a 
                      href={salonConfig.contact.socialMedia.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link instagram"
                    >
                      Instagram
                    </a>
                  )}
                  {salonConfig.contact.socialMedia?.twitter && (
                    <a 
                      href={salonConfig.contact.socialMedia.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link twitter"
                    >
                      Twitter
                    </a>
                  )}
                </div>
                
                <div className="contact-details">
                  <p><strong>Phone:</strong> {salonConfig.contact.phone}</p>
                  <p><strong>Email:</strong> {salonConfig.contact.email}</p>
                  <p><strong>Address:</strong> {salonConfig.contact.address}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="error-message">
            <div className="error-icon">✗</div>
            <h2>Unsubscribe Failed</h2>
            <p>{error}</p>
            
            {salonConfig?.contact && (
              <div className="contact-help">
                <h3>Need Help?</h3>
                <p>Please contact us directly:</p>
                <p><strong>Phone:</strong> {salonConfig.contact.phone}</p>
                <p><strong>Email:</strong> {salonConfig.contact.email}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Unsubscribe;
